import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  AccountBalanceWallet,
  ContentCopy,
  ExitToApp,
  SwapHoriz,
} from '@mui/icons-material';
import { useWallet } from '../contexts/WalletContext';
import { formatAddress, generateAvatarColor, getNetworkName, getNetworkColor } from '../utils/walletUtils';
import { toast } from 'react-toastify';

interface WalletConnectorProps {
  variant?: 'button' | 'chip';
  size?: 'small' | 'medium' | 'large';
  showBalance?: boolean;
  showNetwork?: boolean;
}

const WalletConnector: React.FC<WalletConnectorProps> = ({
  variant = 'chip',
  size = 'medium',
  showBalance = true,
  showNetwork = true,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const {
    isConnecting,
    isConnected,
    account,
    chainId,
    balance,
    currentNetwork,
    supportedNetworks,
    error,
    connect,
    disconnect,
    switchNetwork,
    clearError,
  } = useWallet();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleConnect = async () => {
    clearError();
    await connect();
  };

  const handleDisconnect = async () => {
    await disconnect();
    handleMenuClose();
  };

  const handleCopyAddress = async () => {
    if (account) {
      try {
        await navigator.clipboard.writeText(account);
        toast.success('Address copied to clipboard');
      } catch (err) {
        console.error('Copy failed:', err);
        toast.error('Copy failed');
      }
    }
    handleMenuClose();
  };

  const handleSwitchNetwork = async (targetChainId: number) => {
    await switchNetwork(targetChainId);
    handleMenuClose();
  };

  // Disconnected state
  if (!isConnected || !account) {
    if (variant === 'button') {
      return (
        <Button
          variant="contained"
          startIcon={isConnecting ? <CircularProgress size={16} /> : <AccountBalanceWallet />}
          onClick={handleConnect}
          disabled={isConnecting}
          size={size}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {isConnecting ? 'Connecting...' : (isSmallScreen ? 'Connect Wallet' : 'Connect Wallet')}
        </Button>
      );
    }

    return (
      <Tooltip title={error || 'Click to connect wallet'}>
        <Chip
          label={isSmallScreen ? "Not Connected" : "Wallet Not Connected"}
          color={error ? "error" : "default"}
          variant="outlined"
          size={size === 'large' ? 'medium' : size}
          icon={isConnecting ? <CircularProgress size={16} /> : <AccountBalanceWallet />}
          onClick={handleConnect}
          disabled={isConnecting}
          sx={{
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        />
      </Tooltip>
    );
  }

  // Connected state
  const networkColor = getNetworkColor(chainId);
  const networkName = getNetworkName(chainId);
  const avatarColor = generateAvatarColor(account);
  const formattedAddress = formatAddress(account, isSmallScreen);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

      {/* Wallet Info */}
      <Tooltip title={`${account}${balance ? ` (${balance})` : ''}`}>
        <Box
          onClick={handleMenuOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 0.5,
            borderRadius: 2,
            cursor: 'pointer',
            transition: theme.transitions.create(['background-color'], {
              duration: theme.transitions.duration.short,
            }),
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.08),
            },
          }}
        >
          <Avatar
            sx={{
              width: size === 'small' ? 24 : size === 'large' ? 40 : 32,
              height: size === 'small' ? 24 : size === 'large' ? 40 : 32,
              backgroundColor: avatarColor,
              fontSize: size === 'small' ? '0.75rem' : '1rem',
              fontWeight: 600,
            }}
          >
            {account.slice(2, 4).toUpperCase()}
          </Avatar>
          
          {!isSmallScreen && (
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: theme.palette.text.primary,
                }}
              >
                {formattedAddress}
              </Typography>
              {showBalance && balance && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1,
                  }}
                >
                  {balance}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Tooltip>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            borderRadius: 2,
            boxShadow: theme.shadows[8],
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Network Status */}
        {showNetwork && (
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Current Network
            </Typography>
            <Chip
              label={networkName}
              color={networkColor}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 500,
                fontSize: '0.75rem',
                mt: 0.5,
              }}
            />
          </Box>
        )}
        
        {showNetwork && <Divider />}
        
        {/* Account Info */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Wallet Address
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {account}
          </Typography>
          {balance && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Balance
              </Typography>
              <Typography variant="body2">
                {balance}
              </Typography>
            </>
          )}
        </Box>
        
        <Divider />
        
        {/* Copy Address */}
        <MenuItem onClick={handleCopyAddress} sx={{ py: 1.5 }}>
          <ContentCopy sx={{ mr: 2, fontSize: 20 }} />
          Copy Address
        </MenuItem>
        
        {/* Switch Network */}
        {Object.values(supportedNetworks).length > 1 && (
          <>
            <Divider />
            <Box sx={{ p: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
                Switch Network
              </Typography>
              {Object.values(supportedNetworks).map((network) => (
                <MenuItem
                  key={network.chainId}
                  onClick={() => handleSwitchNetwork(network.chainId)}
                  disabled={chainId === network.chainId}
                  sx={{
                    py: 1,
                    borderRadius: 1,
                    mx: 0.5,
                    backgroundColor: chainId === network.chainId 
                      ? alpha(theme.palette.primary.main, 0.1) 
                      : 'transparent',
                  }}
                >
                  <SwapHoriz sx={{ mr: 2, fontSize: 20 }} />
                  <Box>
                    <Typography variant="body2">{network.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Chain ID: {network.chainId}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Box>
          </>
        )}
        
        <Divider />
        
        {/* Disconnect */}
        <MenuItem 
          onClick={handleDisconnect}
          sx={{
            py: 1.5,
            color: theme.palette.error.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.08),
            },
          }}
        >
          <ExitToApp sx={{ mr: 2, fontSize: 20 }} />
          Disconnect
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default WalletConnector;